import React, { useState, useEffect, useMemo } from 'react';
import { ptvData } from '../data/rtogData';
import { interpolate, findValuesInRange } from '../utils/helpers';
import InputField from '../components/InputField';
import StatusCard from '../components/StatusCard';
import OutputCard from '../components/OutputCard';
import Ci50Graph from '../components/Ci50Graph';
import { CheckCircle, Circle, XCircle } from 'lucide-react';

const StatusText = ({ status }) => {
  const getStatusContent = () => {
    switch(status) {
      case 'pass': return <><CheckCircle size={18} className="mr-2 text-green-500" /> Pass</>;
      case 'acceptable': return <><Circle size={18} className="mr-2 text-yellow-500" /> Acceptable</>;
      case 'fail': return <><XCircle size={18} className="mr-2 text-red-500" /> Fail</>;
      default: return '-';
    }
  };
  const getStatusColour = () => {
    switch(status) {
      case 'pass': return 'text-green-600 dark:text-green-400';
      case 'acceptable': return 'text-yellow-600 dark:text-yellow-400';
      case 'fail': return 'text-red-600 dark:text-red-400';
      default: return 'text-gray-500 dark:text-gray-500';
    }
  };
  return (
    <div className={`mt-2 text-md font-bold flex items-center justify-center ${getStatusColour()}`}>
      {getStatusContent()}
    </div>
  );
};

const AdvancedMetrics = ({ useInstitutionalData, setUseInstitutionalData, institutionalData }) => {
    const [inputs, setInputs] = useState({ ptvVolume: '', v100: '', v50: '', v105: '', totalMUs: '', dosePerFraction: '', doseRate: '', breathHoldEnabled: false, holdTime: '25', recoveryTime: '10' });
    const [outputs, setOutputs] = useState({ ci100: '-', ci50: '-', spillageVolume: '-', ptv15Tolerance: '-', muGyRatio: '-', deliveryTime: '-', muGyRatioFeedback: '-', ci100Status: null, ci50Status: null, spillageStatus: null });
    const [status, setStatus] = useState('');

    const handleInputChange = (field, value) => {
        setInputs(prev => ({ ...prev, [field]: value }));
    };

    useEffect(() => {
        const { ptvVolume, v100, v50, v105, totalMUs, dosePerFraction, doseRate, breathHoldEnabled, holdTime, recoveryTime } = inputs;
        const ptv = parseFloat(ptvVolume);
        const v100Vol = parseFloat(v100);
        const v50Vol = parseFloat(v50);
        const v105Vol = parseFloat(v105);
        const mu = parseFloat(totalMUs);
        const dose = parseFloat(dosePerFraction);
        const rate = parseFloat(doseRate);
        const holdSeconds = parseFloat(holdTime);
        const recoverySeconds = parseFloat(recoveryTime);

        setStatus('');
        let newOutputs = { ci100: '-', ci50: '-', spillageVolume: '-', ptv15Tolerance: '-', muGyRatio: '-', deliveryTime: '-', muGyRatioFeedback: '-', ci100Status: null, ci50Status: null, spillageStatus: null };
        const ci100Tolerance = useInstitutionalData && institutionalData?.ci100Tolerance ? parseFloat(institutionalData.ci100Tolerance) : 1.2;

        if (!isNaN(ptv) && !isNaN(v100Vol) && ptv > 0 && !isNaN(ci100Tolerance)) {
            const ci100Value = v100Vol / ptv;
            newOutputs.ci100 = ci100Value.toFixed(2);
            newOutputs.ci100Status = ci100Value <= ci100Tolerance ? 'pass' : 'fail';
        }

        if (!isNaN(ptv) && !isNaN(v50Vol) && ptv > 0) {
            const ci50Value = v50Vol / ptv;
            newOutputs.ci50 = ci50Value.toFixed(2);
            let tolerance = null;
            const sourceTable = useInstitutionalData && institutionalData?.customTable ? institutionalData.customTable : ptvData;
            
            if(useInstitutionalData && institutionalData?.dataType === 'ranges') {
                const range = findValuesInRange(ptv, sourceTable);
                if(range) {
                    tolerance = { p: parseFloat(range.r50p), v: parseFloat(range.r50v) };
                }
            } else {
                const dataSet = sourceTable.map(d => ({...d, ptv: parseFloat(d.ptv)})).sort((a,b) => a.ptv - b.ptv);
                let lowerPoint = null, upperPoint = null;
                for (let i = 0; i < dataSet.length - 1; i++) {
                    if (ptv >= dataSet[i].ptv && ptv <= dataSet[i + 1].ptv) {
                        lowerPoint = dataSet[i];
                        upperPoint = dataSet[i + 1];
                        break;
                    }
                }
                if (lowerPoint && upperPoint) {
                    tolerance = {
                        p: interpolate(ptv, lowerPoint.ptv, parseFloat(lowerPoint.r50p), upperPoint.ptv, parseFloat(upperPoint.r50p)),
                        v: interpolate(ptv, lowerPoint.ptv, parseFloat(lowerPoint.r50v), upperPoint.ptv, parseFloat(upperPoint.r50v))
                    };
                }
            }
            
            if (tolerance) {
                if (ci50Value <= tolerance.p) { newOutputs.ci50Status = 'pass'; }
                else if (ci50Value <= tolerance.v) { newOutputs.ci50Status = 'acceptable'; }
                else { newOutputs.ci50Status = 'fail'; }
            } else {
                newOutputs.ci50Status = 'n/a';
            }
        }

        if (!isNaN(ptv) && !isNaN(v105Vol) && ptv > 0) {
            const spillageVolume = v105Vol - ptv;
            const ptv15PercentVolume = ptv * 0.15;
            newOutputs.spillageVolume = spillageVolume.toFixed(2);
            newOutputs.ptv15Tolerance = ptv15PercentVolume.toFixed(2);
            newOutputs.spillageStatus = spillageVolume <= ptv15PercentVolume ? 'pass' : 'fail';
        }

        if (!isNaN(mu) && !isNaN(dose) && dose > 0) {
            const muGyRatio = mu / dose;
            newOutputs.muGyRatio = muGyRatio.toFixed(2);
            if (muGyRatio < 230) { newOutputs.muGyRatioFeedback = 'Low'; }
            else if (muGyRatio <= 280) { newOutputs.muGyRatioFeedback = 'Average'; }
            else { newOutputs.muGyRatioFeedback = 'High'; }
        } else {
            newOutputs.muGyRatioFeedback = '-';
        }

        if (!isNaN(mu) && !isNaN(rate) && rate > 0) {
            let deliveryTimeInMinutes = (mu / rate);
            if (breathHoldEnabled && !isNaN(holdSeconds) && !isNaN(recoverySeconds) && holdSeconds > 0) {
                const totalBeamOnSeconds = deliveryTimeInMinutes * 60;
                const numberOfHolds = Math.ceil(totalBeamOnSeconds / holdSeconds);
                const totalRecoverySeconds = numberOfHolds * recoverySeconds;
                const totalDeliverySeconds = totalBeamOnSeconds + totalRecoverySeconds;
                deliveryTimeInMinutes = totalDeliverySeconds / 60;
            }
            newOutputs.deliveryTime = deliveryTimeInMinutes.toFixed(2);
        }
        setOutputs(newOutputs);
    }, [inputs, useInstitutionalData, institutionalData]);
    
    const ci50GraphRanges = useMemo(() => {
        const ptv = parseFloat(inputs.ptvVolume);
        if (isNaN(ptv)) { return { perProtocol: { max: 0 }, acceptable: { max: 0 } }; }
        let r50p = 0;
        let r50v = 0;
        const sourceTable = useInstitutionalData && institutionalData?.customTable ? institutionalData.customTable : ptvData;
        
        const dataSet = sourceTable.map(d => ({...d, ptv: parseFloat(d.ptv)})).sort((a,b) => a.ptv - b.ptv);
        let lowerPoint = null; let upperPoint = null;
        for (let i = 0; i < dataSet.length - 1; i++) {
            if (ptv >= dataSet[i].ptv && ptv <= dataSet[i+1].ptv) {
                lowerPoint = dataSet[i];
                upperPoint = dataSet[i+1];
                break;
            }
        }
        if (lowerPoint && upperPoint) {
            r50p = interpolate(ptv, lowerPoint.ptv, parseFloat(lowerPoint.r50p), upperPoint.ptv, parseFloat(upperPoint.r50p));
            r50v = interpolate(ptv, lowerPoint.ptv, parseFloat(lowerPoint.r50v), upperPoint.ptv, parseFloat(upperPoint.r50v));
        }
        return { perProtocol: { max: r50p || 0 }, acceptable: { max: r50v || 0 } };
    }, [inputs.ptvVolume, useInstitutionalData, institutionalData]);

    return (
        <div>
            <div className="text-center mb-8">
                <h1 className="text-2xl md:text-4xl font-bold text-gray-900 dark:text-cyan-400 mb-2">Plan Metric Analysis</h1>
                <p className="text-gray-600 dark:text-gray-400 text-base md:text-lg">Calculate key conformity indices, MU/Gy ratio, and estimate treatment time.</p>
            </div>
            <div className="flex items-center justify-center mb-6">
                <label className="flex items-center text-gray-700 dark:text-gray-300 cursor-pointer">
                    <input type="checkbox" checked={useInstitutionalData} onChange={() => setUseInstitutionalData(!useInstitutionalData)} className="form-checkbox h-5 w-5 text-blue-600" />
                    <span className="ml-2 font-semibold">Use My Institutional Data</span>
                </label>
            </div>
            
            <div className="p-4 md:p-8 bg-gray-100 dark:bg-gray-800 rounded-xl mb-8 border border-gray-300 dark:border-gray-700 shadow-lg">
                <h2 className="text-xl md:text-2xl font-bold text-blue-800 dark:text-blue-400 mb-4">Calculate Conformity & Spillage</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
                    <InputField label="PTV Volume (cc)" value={inputs.ptvVolume} onChange={e => handleInputChange('ptvVolume', e.target.value)} type="number" step="0.1" />
                    <InputField label="V100% Volume (cc)" value={inputs.v100} onChange={e => handleInputChange('v100', e.target.value)} type="number" step="0.1" />
                    <InputField label="V50% Volume (cc)" value={inputs.v50} onChange={e => handleInputChange('v50', e.target.value)} type="number" step="0.1" />
                    <InputField label="V105% Volume (cc)" value={inputs.v105} onChange={e => handleInputChange('v105', e.target.value)} type="number" step="0.1" />
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <StatusCard label="CI100" value={outputs.ci100} status={outputs.ci100Status} />
                <StatusCard label="CI50" value={outputs.ci50} status={outputs.ci50Status} />
                <div className="bg-green-100 dark:bg-green-900/20 rounded-xl p-6 shadow-md border border-green-300 dark:border-green-900">
                    <h3 className="text-md text-green-700 dark:text-green-300 font-semibold mb-2 text-center">105% Spillage Check</h3>
                    <div className="flex justify-between items-center mt-2 mb-2">
                        <span className="text-sm md:text-base">105% Spillage Volume (cc):</span>
                        <span className="font-bold text-green-800 dark:text-green-400">{outputs.spillageVolume}</span>
                    </div>
                    <div className="flex justify-between items-center mb-2">
                        <span className="text-sm md:text-base">15% PTV Volume (cc):</span>
                        <span className="font-bold text-green-800 dark:text-green-400">{outputs.ptv15Tolerance}</span>
                    </div>
                    <StatusText status={outputs.spillageStatus} />
                </div>
            </div>

            <div className="border-t border-gray-300 dark:border-gray-700 pt-8 mt-8">
                <h2 className="text-xl md:text-2xl font-bold text-center text-gray-900 dark:text-cyan-400 mb-6">RTOG Protocol CI50 Ranges</h2>
                <Ci50Graph ci50Value={parseFloat(outputs.ci50)} ranges={ci50GraphRanges} />
            </div>

            <div className="p-4 md:p-8 bg-gray-100 dark:bg-gray-800 rounded-xl mt-8 mb-8 border border-gray-300 dark:border-gray-700 shadow-lg">
                <h2 className="text-xl md:text-2xl font-bold text-blue-800 dark:text-blue-400 mb-4">Calculate MU/Gy & Treatment Time</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <InputField label="Total Plan MUs" value={inputs.totalMUs} onChange={e => handleInputChange('totalMUs', e.target.value)} type="number" step="1" />
                    <InputField label="Dose per Fraction (Gy)" value={inputs.dosePerFraction} onChange={e => handleInputChange('dosePerFraction', e.target.value)} type="number" step="0.1" />
                    <InputField label="Max Dose Rate (MU/min)" value={inputs.doseRate} onChange={e => handleInputChange('doseRate', e.target.value)} type="number" step="1" />
                </div>
                <div className="flex items-center mt-6 p-4 bg-gray-200 dark:bg-gray-700 rounded-lg">
                    <input type="checkbox" id="breathHold" checked={inputs.breathHoldEnabled} onChange={e => handleInputChange('breathHoldEnabled', e.target.checked)} className="form-checkbox h-5 w-5" />
                    <label htmlFor="breathHold" className="ml-2 font-semibold cursor-pointer">Enable Breath-Hold Calculation</label>
                </div>
                {inputs.breathHoldEnabled && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                        <InputField label="Hold Time (seconds)" value={inputs.holdTime} onChange={e => handleInputChange('holdTime', e.target.value)} type="number" step="1" min="1" />
                        <InputField label="Recovery Time (seconds)" value={inputs.recoveryTime} onChange={e => handleInputChange('recoveryTime', e.target.value)} type="number" step="1" min="0" />
                    </div>
                )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
                <OutputCard label="MU/Gy Ratio" value={outputs.muGyRatio} colour="purple" feedback={outputs.muGyRatioFeedback} />
                <OutputCard label="Estimated Delivery Time (min)" value={outputs.deliveryTime} colour="indigo" />
            </div>
            {status && <div className="mt-8 text-center text-md md:text-lg font-medium text-red-500">{status}</div>}
        </div>
    );
};

export default AdvancedMetrics;